import {formatInTimeZone, toDate} from "date-fns-tz";
import {TZDate} from "react-day-picker";
import {Frequency, RRule, rrulestr} from "rrule";

import {formatDateTime} from "@/lib/format.tsx";
import {MaintenanceSchedule, MaintenanceScheduleType} from "@/lib/types/maintenance.ts";
import {MaintenanceScheduleFormData} from "@/pages/maintenance/maintenance-create/CreateEditMaintenanceScheduleForm.types.ts";
import {buildRRuleString} from "@/pages/maintenance/maintenance-create/CreateEditMaintenanceScheduleForm.utils.tsx";
import {
  DurationUnitDisplay,
  FrequencyType,
} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.types.ts";

// rrule.js has no timezone support — it runs all recurrence math on a date's UTC
// fields (https://github.com/jkbrzt/rrule/issues/556#issuecomment-1264585649). To
// evaluate a rule on the account's calendar — matching the backend, see
// apps/maintenance/applogic.py generate_occurrence_rrule_start_datetimes — we shift
// each date so its UTC fields carry the account-local wall clock before handing it to
// rrule, then shift the results back. Unlike the getTimezoneOffset() trick in that
// issue, we anchor to the account's IANA zone (not the browser's) and resolve the
// offset per-date, so it stays correct across DST boundaries.

// Account instant -> a Date whose UTC fields hold the account-local wall clock.
const toRRuleInput = (date: Date, timezone: string): Date => {
  const zoned = new TZDate(date, timezone);
  return new Date(
    Date.UTC(
      zoned.getFullYear(),
      zoned.getMonth(),
      zoned.getDate(),
      zoned.getHours(),
      zoned.getMinutes(),
      zoned.getSeconds()
    )
  );
};

// Reverse: read the UTC-field wall clock and re-interpret it in the account zone.
const fromRRuleOutput = (date: Date, timezone: string): Date =>
  toDate(formatInTimeZone(date, "UTC", "yyyy-MM-dd HH:mm:ss"), {timeZone: timezone});

// Next occurrence of an rrule string, evaluated in the account timezone and returned as
// a real instant — mirroring how the backend generates occurrences.
export const getNextRruleOccurrenceInTz = (
  rruleString: string,
  startsAt: Date,
  timezone: string,
  from: Date = new Date()
): Date | null => {
  const parsed = rrulestr(rruleString) as RRule;
  const {until} = parsed.origOptions;
  const rule = new RRule({
    ...parsed.origOptions,
    dtstart: toRRuleInput(startsAt, timezone),
    until: until ? toRRuleInput(until, timezone) : until,
  });
  const next = rule.after(toRRuleInput(from, timezone));
  return next ? fromRRuleOutput(next, timezone) : null;
};

const getEndOfMonthDayReplacement = (bymonthday: number[]): string | null => {
  if (bymonthday.length < 2) return null;
  const sorted = [...bymonthday].sort((a, b) => a - b);
  const max = sorted[sorted.length - 1];
  if (max < 29 || max > 31) return null;
  const expected = Array.from({length: max - 28 + 1}, (_, i) => 28 + i);
  if (expected.length !== sorted.length || expected.some((day, i) => day !== sorted[i])) {
    return null;
  }
  if (max === 31) {
    return "last day of the month";
  }
  return `${max}th or the last day of the month`;
};

const rruleToText = (rruleString: string) => {
  const rrule = rrulestr(rruleString);
  rrule.options.until = null;

  const isMonthly = rrule.options.freq === Frequency.MONTHLY;
  const bysetpos = rrule.options.bysetpos ?? [];
  const bymonthday = rrule.options.bymonthday ?? [];
  const endOfMonthReplacement =
    isMonthly && bysetpos.length === 1 && bysetpos[0] === -1 ? getEndOfMonthDayReplacement(bymonthday) : null;

  if (endOfMonthReplacement) {
    rrule.options.bymonthday = [];
    rrule.options.bysetpos = [];
  }

  let text = rrule.toText();

  text = text.replace(/every week/gi, "Weekly");
  text = text.replace(/every day/gi, "Daily");
  text = text.replace(/every month/gi, "Monthly");
  text = text.replace(/every year/gi, "Yearly");
  text = text.replace(/every hour/gi, "Hourly");
  text = text.replace(/every minute/gi, "Every minute");
  text = text.replace(/every/gi, "Every");

  text = text.replace(/\bMonday\b/g, "Mon");
  text = text.replace(/\bTuesday\b/g, "Tue");
  text = text.replace(/\bWednesday\b/g, "Wed");
  text = text.replace(/\bThursday\b/g, "Thu");
  text = text.replace(/\bFriday\b/g, "Fri");
  text = text.replace(/\bSaturday\b/g, "Sat");
  text = text.replace(/\bSunday\b/g, "Sun");

  if (endOfMonthReplacement) {
    text = `${text} on the ${endOfMonthReplacement}`;
  }

  return text;
};

export const getRecurrenceDisplay = (schedule: Pick<MaintenanceSchedule, "schedule_type" | "rrule">) => {
  if (
    schedule.schedule_type === MaintenanceScheduleType.ONE_OFF ||
    schedule.schedule_type === MaintenanceScheduleType.MANUAL
  ) {
    return "Doesn't repeat";
  }
  return schedule.rrule ? rruleToText(schedule.rrule) : "—";
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], {hour: "numeric", minute: "2-digit"});
};

export const formatStartDescription = (formData: MaintenanceScheduleFormData, timezone: string): string | null => {
  if (!formData.start_date) return null;

  if (formData.frequency_type === FrequencyType.DONT_REPEAT) {
    return `One-time maintenance on ${formatDateTime(formData.start_date, timezone)}`;
  }

  const rruleString = buildRRuleString(formData, timezone);
  if (!rruleString) return null;

  const recurrenceText = getRecurrenceDisplay({
    schedule_type: MaintenanceScheduleType.RRULE,
    rrule: rruleString,
  });
  return `Maintenance ${recurrenceText} at ${formatTime(formData.start_date)}`;
};

export const getDurationDescription = (formData: MaintenanceScheduleFormData): string => {
  const value = formData.duration_value;
  const unit = formData.duration_unit;
  const label = DurationUnitDisplay[unit].toLowerCase();
  return `${value} ${value === 1 ? label.slice(0, -1) : label}`;
};

export const getNextOccurrenceFromForm = (formData: MaintenanceScheduleFormData, timezone?: string): Date | null => {
  if (!formData.start_date) return null;

  if (formData.frequency_type === FrequencyType.DONT_REPEAT) {
    return formData.start_date > new Date() ? formData.start_date : null;
  }

  const rruleString = buildRRuleString(formData, timezone);
  if (!rruleString) return null;

  try {
    if (!timezone) {
      return rrulestr(rruleString).after(new Date());
    }
    return getNextRruleOccurrenceInTz(rruleString, formData.start_date, timezone);
  } catch {
    return null;
  }
};
